import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FiArrowLeft, FiBook, FiBriefcase, FiMonitor, FiHeart, 
  FiTool, FiCoffee, FiTruck, FiSettings, 
  FiCamera, FiDollarSign, FiClipboard 
} from 'react-icons/fi';
import { CourseCard } from '../components/courses';
import { getCategoryBySlug, getCoursesByCategory } from '../services/api';

const Container = styled.div`
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 2rem;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    transform: translateX(-4px);
  }

  svg {
    font-size: 1.2rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const CategoryIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${props => props.color} 0%, ${props => props.color}dd 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
  box-shadow: 0 8px 16px ${props => props.color}40;
`;

const Title = styled.h1`
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Description = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
  max-width: 600px;
  margin: 0 auto 2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CourseCount = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.color}20;
  color: ${props => props.color};
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  border: 2px solid ${props => props.color}30;

  svg {
    font-size: 1.2rem;
  }
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.colors.error};
  font-size: 1.1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.1rem;

  svg {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

const CategoryPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch category details
        const categoryData = await getCategoryBySlug(slug);
        setCategory(categoryData);

        // Fetch courses for this category
        const coursesData = await getCoursesByCategory(slug);
        setCourses(coursesData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load category data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchData();
    }
  }, [slug]);

  const getIconComponent = (iconName) => {
    const iconMap = {
      'FiBriefcase': <FiBriefcase />,
      'FiMonitor': <FiMonitor />,
      'FiHeart': <FiHeart />,
      'FiBook': <FiBook />,
      'FiTool': <FiTool />,
      'FiCoffee': <FiCoffee />,
      'FiTruck': <FiTruck />,
      'FiSettings': <FiSettings />,
      'FiLeaf': <FiTool />,
      'FiCamera': <FiCamera />,
      'FiDollarSign': <FiDollarSign />
    };
    return iconMap[iconName] || <FiClipboard />;
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          Loading category...
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          {error}
        </ErrorContainer>
      </Container>
    );
  }

  if (!category) {
    return (
      <Container>
        <ErrorContainer>
          Category not found.
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton to="/courses">
        <FiArrowLeft />
        Back to All Categories
      </BackButton>

      <Header>
        <CategoryIcon color={category.color}>
          {getIconComponent(category.icon)}
        </CategoryIcon>
        
        <Title>{category.name}</Title>
        
        <Description>{category.description}</Description>
        
        <CourseCount color={category.color}>
          <FiBook />
          {courses.length} {courses.length === 1 ? 'Course' : 'Courses'} Available
        </CourseCount>
      </Header>

      {courses.length === 0 ? (
        <EmptyState>
          <FiBook />
          <p>No courses available in this category at the moment.</p>
          <p>Please check back later or explore other categories.</p>
        </EmptyState>
      ) : (
        <CoursesGrid>
          {courses.map((course) => (
            <CourseCard
              key={course._id}
              course={course}
              categoryColor={category.color}
            />
          ))}
        </CoursesGrid>
      )}
    </Container>
  );
};

export default CategoryPage; 